/**
 * Opens data the server sealed under the user's public key, in the browser.
 *
 * Mirrors backend/app/services/vault.py. A blob is {c, k}:
 *   k = RSA-OAEP (SHA-1, as PyCryptodome's default) wrap of a random AES-256 key
 *   c = base64(nonce[16] || tag[16] || ciphertext) of AES-256-GCM over UTF-8 JSON
 *
 * Two shapes are opened wherever they appear in a response:
 *   obj.sealed = {c, k}               → its fields are merged into obj, obj._opened = true
 *   obj.sealed_values = {field: {c,k}} → obj[field] = the sealed {v} value
 *
 * `_opened` matters when an entry is sent back: the server reseals an opened
 * entry from its plaintext and keeps the blob of one that wasn't, so saving
 * from a locked page can't wipe what it couldn't read.
 *
 * The private key never leaves the browser for this. It's the PKCS#1 PEM kept
 * in sessionStorage; WebCrypto only imports PKCS#8, so it's wrapped first.
 */

const b64ToBytes = (b64) => Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));

const derLength = (n) => {
  if (n < 0x80) return [n];
  const out = [];
  let x = n;
  while (x > 0) { out.unshift(x & 0xff); x >>= 8; }
  return [0x80 | out.length, ...out];
};

const derTlv = (tag, body) => new Uint8Array([tag, ...derLength(body.length), ...body]);

// AlgorithmIdentifier { rsaEncryption, NULL }
const RSA_ALG_ID = new Uint8Array([
  0x30, 0x0d, 0x06, 0x09, 0x2a, 0x86, 0x48, 0x86, 0xf7, 0x0d, 0x01, 0x01, 0x01, 0x05, 0x00,
]);

/** PKCS#1 RSAPrivateKey DER → PKCS#8 PrivateKeyInfo DER. Exported for tests. */
export const pkcs1ToPkcs8 = (pkcs1) => {
  const version = new Uint8Array([0x02, 0x01, 0x00]);
  const octet = derTlv(0x04, pkcs1);
  const body = new Uint8Array(version.length + RSA_ALG_ID.length + octet.length);
  body.set(version, 0);
  body.set(RSA_ALG_ID, version.length);
  body.set(octet, version.length + RSA_ALG_ID.length);
  return derTlv(0x30, body);
};

const pemBody = (pem) => b64ToBytes(pem.replace(/-----[^-]+-----/g, '').replace(/\s+/g, ''));

let cachedPem = null;
let cachedKey = null;

const importPrivateKey = async (pem) => {
  if (cachedPem === pem && cachedKey) return cachedKey;
  const der = pem.includes('BEGIN RSA PRIVATE KEY') ? pkcs1ToPkcs8(pemBody(pem)) : pemBody(pem);
  cachedKey = await crypto.subtle.importKey(
    'pkcs8', der, { name: 'RSA-OAEP', hash: 'SHA-1' }, false, ['decrypt'],
  );
  cachedPem = pem;
  return cachedKey;
};

// Opened blobs by ciphertext, so the dashboard's 30s poll doesn't redo the
// RSA work. In memory only; bounded.
const opened = new Map();
const MAX_CACHE = 5000;

/** Decrypt one {c, k} blob to its JSON value. */
export const openBlob = async (blob, pem) => {
  const hit = opened.get(blob.c);
  if (hit) return hit;
  const promise = (async () => {
    const key = await importPrivateKey(pem);
    const aesRaw = await crypto.subtle.decrypt({ name: 'RSA-OAEP' }, key, b64ToBytes(blob.k));
    const aes = await crypto.subtle.importKey('raw', aesRaw, { name: 'AES-GCM' }, false, ['decrypt']);
    const raw = b64ToBytes(blob.c);
    const nonce = raw.slice(0, 16);
    const tag = raw.slice(16, 32);
    const ct = raw.slice(32);
    const withTag = new Uint8Array(ct.length + tag.length);
    withTag.set(ct);
    withTag.set(tag, ct.length);
    const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: nonce, tagLength: 128 }, aes, withTag);
    return JSON.parse(new TextDecoder().decode(plain));
  })();
  if (opened.size >= MAX_CACHE) opened.clear();
  opened.set(blob.c, promise);
  promise.catch(() => opened.delete(blob.c));
  return promise;
};

export const isBlob = (v) => !!v && typeof v === 'object' && typeof v.c === 'string' && typeof v.k === 'string';

/** Every place in `data` that needs opening, as [kind, obj, field?]. */
const collect = (data, out = [], seen = new Set()) => {
  if (!data || typeof data !== 'object' || seen.has(data)) return out;
  seen.add(data);
  if (Array.isArray(data)) {
    data.forEach((v) => collect(v, out, seen));
    return out;
  }
  if (isBlob(data.sealed) && !data._opened) out.push(['entry', data]);
  if (data.sealed_values && typeof data.sealed_values === 'object') {
    Object.entries(data.sealed_values).forEach(([field, blob]) => {
      if (isBlob(blob)) out.push(['value', data, field]);
    });
  }
  Object.entries(data).forEach(([k, v]) => {
    if (k !== 'sealed' && k !== 'sealed_values') collect(v, out, seen);
  });
  return out;
};

/** Whether `data` has anything sealed still to open. */
export const hasSealed = (data) => collect(data).length > 0;

/**
 * Open everything sealed in `data`, in place. Returns how many couldn't be
 * opened (wrong key, e.g. after switching accounts) — those are left as they
 * were, without `_opened`.
 */
export const openSealedTree = async (data, pem) => {
  const todo = collect(data);
  let failed = 0;
  await Promise.all(todo.map(async ([kind, obj, field]) => {
    try {
      if (kind === 'entry') {
        const values = await openBlob(obj.sealed, pem);
        Object.assign(obj, values, { _opened: true });
      } else {
        const values = await openBlob(obj.sealed_values[field], pem);
        obj[field] = values ? values.v : undefined;
      }
    } catch {
      failed += 1;
    }
  }));
  return failed;
};

/** Forget the imported key and opened values (sign-out, account switch). */
export const clearVaultCache = () => {
  opened.clear();
  cachedPem = null;
  cachedKey = null;
};
